// Accounting — daily income / expense ledger for the user's own business
// entities (Mercy Consultancy, Maro, Mercy Success Sdn Bhd, Nikko, etc.).
// Admin-by-default; grantable to staff via modules_allowed: ["accounting"].

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { api, API } from "../api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Calculator, Plus, RefreshCw, Search, Trash2, Edit3, X,
  TrendingUp, TrendingDown, Building2, Receipt, Calendar,
  Repeat, Bell, AlertTriangle, ChevronRight, FileText, Tag,
  ArrowUpRight, ArrowDownRight, BarChart3, Play,
} from "lucide-react";
import { useTrackedSearch, trackTab } from "../activityTracker";

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Cheque", "Card", "Online", "Other"];
const FREQUENCIES = ["daily", "weekly", "monthly", "yearly"];

// ---------- helpers ----------
function fmtMoney(n) {
  return `RM ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}
function thisMonth() {
  return new Date().toISOString().slice(0, 7);
}
async function fileToB64(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || "").split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function Accounting() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [tab, setTab] = useState("entries"); // entries | recurring | companies | categories
  const switchTab = (t) => { setTab(t); try { trackTab("accounting", t); } catch { /* noop */ } };
  const [month, setMonth] = useState(thisMonth());
  const [companyFilter, setCompanyFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  useTrackedSearch("accounting", q);
  const [typeFilter, setTypeFilter] = useState("");

  // Dialog state
  const [entryDlg, setEntryDlg] = useState(null);
  const [companyDlg, setCompanyDlg] = useState(null);
  const [recurDlg, setRecurDlg] = useState(null);
  const [categoryDlg, setCategoryDlg] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [from, to] = monthRange(month);
      const entryParams = { from_date: from, to_date: to };
      if (companyFilter) entryParams.company_id = companyFilter;
      if (typeFilter) entryParams.type = typeFilter;
      if (q) entryParams.q = q;
      const summaryParams = { month };
      if (companyFilter) summaryParams.company_id = companyFilter;
      const [s, cl, ct, ent, rec] = await Promise.all([
        api.get("/accounting/summary", { params: summaryParams }),
        api.get("/accounting/companies"),
        api.get("/accounting/categories"),
        api.get("/accounting/entries", { params: entryParams }),
        api.get("/accounting/recurring"),
      ]);
      setSummary(s.data);
      setCompanies(cl.data || []);
      setCategories(ct.data || []);
      setEntries(ent.data || []);
      setRecurring(rec.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load accounting");
    } finally {
      setBusy(false);
    }
  }, [month, companyFilter, q, typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="Accounting — Daily Cashbook">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-emerald-600/20 border border-emerald-600/40 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold tracking-tight">Accounting</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Daily income &amp; expense ledger across your business entities. Auto-generates recurring entries &amp; WhatsApp reminders.
              {!isOwner && (
                <span className="ml-2 text-amber-400">· Only the owner can delete records — to remove an entry, ask the owner.</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 h-9 w-[150px] num"
              data-testid="acc-month-input" />
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
              className="bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm"
              data-testid="acc-company-filter">
              <option value="">All companies</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={load} disabled={busy}
              className="h-9 border-slate-700">
              <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stat cards — always rendered, fall back to zeros until summary loads */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatCard label="Income" value={fmtMoney(summary?.totals?.income || 0)} icon={ArrowUpRight} tone="emerald"
            testid="acc-stat-income" />
          <StatCard label="Expense" value={fmtMoney(summary?.totals?.expense || 0)} icon={ArrowDownRight} tone="red"
            testid="acc-stat-expense" />
          <StatCard label="Net" value={fmtMoney(summary?.totals?.net || 0)}
            icon={(summary?.totals?.net || 0) >= 0 ? TrendingUp : TrendingDown}
            tone={(summary?.totals?.net || 0) >= 0 ? "emerald" : "red"}
            testid="acc-stat-net" />
          <StatCard label="Entries" value={summary?.totals?.entries || 0} icon={Receipt} tone="slate"
            testid="acc-stat-entries" sub={`incl. ${fmtMoney(summary?.totals?.tax || 0)} tax`} />
          <StatCard
            label={(summary?.recurring?.overdue || 0) > 0 ? "Recurring overdue" : "Recurring due ≤7d"}
            value={(summary?.recurring?.overdue || 0) || (summary?.recurring?.due_within_7d || 0)}
            icon={(summary?.recurring?.overdue || 0) > 0 ? AlertTriangle : Bell}
            tone={(summary?.recurring?.overdue || 0) > 0 ? "red" : "amber"}
            testid="acc-stat-recurring" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-800">
          <TabBtn active={tab === "entries"} onClick={() => switchTab("entries")}
            icon={Receipt} testid="acc-tab-entries">Entries</TabBtn>
          <TabBtn active={tab === "recurring"} onClick={() => switchTab("recurring")}
            icon={Repeat} testid="acc-tab-recurring">Recurring</TabBtn>
          <TabBtn active={tab === "companies"} onClick={() => switchTab("companies")}
            icon={Building2} testid="acc-tab-companies">Companies</TabBtn>
          <TabBtn active={tab === "categories"} onClick={() => switchTab("categories")}
            icon={Tag} testid="acc-tab-categories">Categories</TabBtn>
        </div>

        {tab === "entries" && (
          <EntriesTab
            entries={entries}
            summary={summary}
            companies={companies}
            q={q} setQ={setQ}
            typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            isOwner={isOwner}
            onNew={() => setEntryDlg({ mode: "new" })}
            onEdit={(e) => setEntryDlg({ mode: "edit", row: e })}
            onDelete={async (e) => {
              if (!window.confirm(`Delete this ${e.type} entry of ${fmtMoney(e.amount)}?\n\n"${e.description}"`)) return;
              try {
                await api.delete(`/accounting/entries/${e.id}`);
                toast.success("Deleted");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
          />
        )}

        {tab === "recurring" && (
          <RecurringTab
            recurring={recurring}
            companies={companies}
            isOwner={isOwner}
            onNew={() => setRecurDlg({ mode: "new" })}
            onEdit={(r) => setRecurDlg({ mode: "edit", row: r })}
            onDelete={async (r) => {
              if (!window.confirm(`Delete recurring rule "${r.name}"?`)) return;
              try {
                await api.delete(`/accounting/recurring/${r.id}`);
                toast.success("Removed");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
            onRunNow={async (r) => {
              if (!window.confirm(`Generate the next "${r.name}" entry now?\n\nThis creates a real ledger row for ${r.next_due_date} and advances the schedule.`)) return;
              try {
                const { data } = await api.post(`/accounting/recurring/${r.id}/run-now`);
                if (data?.skipped) {
                  toast.message(`Already generated for ${r.next_due_date}`);
                } else {
                  toast.success(`Entry created — next due ${data.next_due_date}`);
                }
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
            onToggle={async (r) => {
              try {
                await api.put(`/accounting/recurring/${r.id}`, { is_active: !r.is_active });
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
          />
        )}

        {tab === "companies" && (
          <CompaniesTab
            companies={companies}
            isOwner={isOwner}
            onNew={() => setCompanyDlg({ mode: "new" })}
            onEdit={(c) => setCompanyDlg({ mode: "edit", row: c })}
            onDelete={async (c) => {
              if (!window.confirm(`Delete company "${c.name}"?`)) return;
              try {
                await api.delete(`/accounting/companies/${c.id}`);
                toast.success("Removed");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
          />
        )}

        {tab === "categories" && (
          <CategoriesTab
            categories={categories}
            isOwner={isOwner}
            onNew={() => setCategoryDlg(true)}
            onDelete={async (c) => {
              if (!window.confirm(`Disable category "${c.name}"?\n\nExisting entries keep the label.`)) return;
              try {
                await api.delete(`/accounting/categories/${c.id}`);
                toast.success("Disabled");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            }}
          />
        )}
      </div>

      {/* Dialogs */}
      <EntryDialog open={!!entryDlg} state={entryDlg} onClose={() => setEntryDlg(null)}
        onSaved={() => { setEntryDlg(null); load(); }}
        companies={companies} categories={categories} />
      <CompanyDialog open={!!companyDlg} state={companyDlg} onClose={() => setCompanyDlg(null)}
        onSaved={() => { setCompanyDlg(null); load(); }} />
      <RecurringDialog open={!!recurDlg} state={recurDlg} onClose={() => setRecurDlg(null)}
        onSaved={() => { setRecurDlg(null); load(); }}
        companies={companies} categories={categories} />
      <CategoryDialog open={!!categoryDlg} onClose={() => setCategoryDlg(false)}
        onSaved={() => { setCategoryDlg(false); load(); }} />
    </Layout>
  );
}

// ============================================================================
// helpers
// ============================================================================
function monthRange(month) {
  const [y, m] = month.split("-").map(Number);
  const first = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDate = new Date(y, m, 0);  // day 0 of next month = last day of this month
  const last = lastDate.toISOString().slice(0, 10);
  return [first, last];
}

function StatCard({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  const toneCls = {
    emerald: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    red: "border-red-500/40 text-red-300 bg-red-500/10",
    amber: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    slate: "border-slate-500/40 text-slate-300 bg-slate-500/10",
  }[tone];
  return (
    <div data-testid={testid}
      className="rounded-lg border hairline surface-1 p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center border ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-semibold num tabular-nums mt-0.5 truncate">{value}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-4 text-sm font-medium border-b-2 -mb-px transition flex items-center gap-1.5 whitespace-nowrap
        ${active ? "border-emerald-500 text-emerald-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}

// ============================================================================
// Entries tab — list + category breakdown side-by-side
// ============================================================================
function EntriesTab({ entries, summary, companies, q, setQ, typeFilter, setTypeFilter, isOwner, onNew, onEdit, onDelete }) {
  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Entries list */}
      <div className="col-span-12 lg:col-span-8">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search description, vendor, reference…"
              className="bg-[#0a0e1a] border-slate-800 pl-9 h-9"
              data-testid="acc-search" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm"
            data-testid="acc-type-filter">
            <option value="">All types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <Button onClick={onNew} className="h-9 bg-emerald-600 hover:bg-emerald-500"
            data-testid="acc-new-entry-btn">
            <Plus className="w-4 h-4 mr-1" /> New entry
          </Button>
        </div>

        {entries.length === 0 ? (
          <div className="rounded-lg border hairline surface-1 text-center py-14 px-6">
            <Receipt className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <div className="text-slate-300 text-sm font-medium">No entries this month.</div>
            <div className="text-xs text-slate-500 mt-1">
              Click <span className="text-emerald-300">New entry</span> or set up a recurring rule.
            </div>
          </div>
        ) : (
          <div className="rounded-lg border hairline surface-1 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2 w-24">Date</th>
                  <th className="text-left px-3 py-2 w-16">Type</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Company</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Category</th>
                  <th className="text-right px-3 py-2 w-32">Amount</th>
                  <th className="text-right px-3 py-2 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}
                    data-testid={`acc-entry-row-${e.id}`}
                    className="border-t border-slate-800/60 hover:bg-slate-800/20">
                    <td className="px-3 py-2 num text-slate-300">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">
                      {e.type === "income" ? (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">IN</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">OUT</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-200 truncate" title={e.description}>{e.description}</div>
                      {e.vendor_name && <div className="text-[10px] text-slate-500">{e.vendor_name}</div>}
                      {e.recurring_id && (
                        <div className="text-[9px] text-slate-600 flex items-center gap-0.5 mt-0.5">
                          <Repeat className="w-2.5 h-2.5" /> auto from recurring
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-400 hidden md:table-cell truncate">{e.company_name}</td>
                    <td className="px-3 py-2 text-slate-400 hidden lg:table-cell">{e.category || "—"}</td>
                    <td className={`px-3 py-2 text-right num tabular-nums font-medium ${e.type === "income" ? "text-emerald-300" : "text-red-300"}`}>
                      {e.type === "income" ? "+" : "−"} {fmtMoney(e.amount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {e.receipt_filename && (
                        <a href={`${API}/accounting/entries/${e.id}/receipt`}
                          target="_blank" rel="noreferrer"
                          title={e.receipt_filename}
                          className="inline-block p-1 text-slate-400 hover:text-slate-100">
                          <FileText className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button onClick={() => onEdit(e)}
                        className="inline-block p-1 text-slate-400 hover:text-slate-100"
                        data-testid={`acc-edit-${e.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      {isOwner && (
                        <button onClick={() => onDelete(e)}
                          className="inline-block p-1 text-red-400 hover:text-red-300"
                          data-testid={`acc-delete-${e.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Side panel: per-company & per-category breakdown */}
      <div className="col-span-12 lg:col-span-4 space-y-3">
        {summary && summary.by_company.length > 0 && (
          <div className="rounded-lg border hairline surface-1 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Per company
            </div>
            <div className="space-y-2">
              {summary.by_company.map((row) => (
                <div key={row.company} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 truncate pr-2">{row.company}</span>
                    <span className={`num tabular-nums font-medium ${row.net >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {fmtMoney(row.net)}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 num flex gap-3">
                    <span>+ {fmtMoney(row.income)}</span>
                    <span>− {fmtMoney(row.expense)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {summary && summary.by_category.length > 0 && (
          <div className="rounded-lg border hairline surface-1 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> Top categories
            </div>
            <div className="space-y-1.5">
              {summary.by_category.slice(0, 10).map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0
                      ${row.type === "income" ? "bg-emerald-400" : "bg-red-400"}`}></span>
                    <span className="text-slate-300 truncate">{row.category}</span>
                  </div>
                  <span className={`num tabular-nums ${row.type === "income" ? "text-emerald-300" : "text-red-300"}`}>
                    {fmtMoney(row.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Recurring rules tab
// ============================================================================
function RecurringTab({ recurring, companies, isOwner, onNew, onEdit, onDelete, onRunNow, onToggle }) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400">
          Auto-generates ledger entries on schedule. Reminders fire via WhatsApp (admins + staff with the
          <span className="text-emerald-300"> accounting</span> module) X days before each due date.
        </div>
        <Button onClick={onNew} className="h-9 bg-emerald-600 hover:bg-emerald-500"
          data-testid="acc-new-recurring-btn">
          <Plus className="w-4 h-4 mr-1" /> New rule
        </Button>
      </div>
      {recurring.length === 0 ? (
        <div className="rounded-lg border hairline surface-1 text-center py-14 px-6">
          <Repeat className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <div className="text-slate-300 text-sm font-medium">No recurring rules yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Set up rent, utilities, salary or any other repeating expense / income.
          </div>
        </div>
      ) : (
        <div className="rounded-lg border hairline surface-1 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Company</th>
                <th className="text-left px-3 py-2 w-24">Frequency</th>
                <th className="text-left px-3 py-2 w-28">Next due</th>
                <th className="text-right px-3 py-2 w-28">Amount</th>
                <th className="text-right px-3 py-2 w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recurring.map((r) => {
                const today = new Date().toISOString().slice(0, 10);
                const overdue = r.is_active && r.next_due_date < today;
                return (
                  <tr key={r.id}
                    data-testid={`acc-recurring-row-${r.id}`}
                    className={`border-t border-slate-800/60 hover:bg-slate-800/20 ${!r.is_active ? "opacity-50" : ""}`}>
                    <td className="px-3 py-2">
                      <div className="text-slate-200">{r.name}</div>
                      <div className="text-[10px] text-slate-500">
                        {r.type === "income" ? "➕ Income" : "➖ Expense"} · {r.description}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-400 hidden md:table-cell">{r.company_name}</td>
                    <td className="px-3 py-2 text-slate-300 capitalize">{r.frequency}</td>
                    <td className={`px-3 py-2 num ${overdue ? "text-red-300 font-medium" : "text-slate-300"}`}>
                      {fmtDate(r.next_due_date)}
                      {overdue && <div className="text-[9px] uppercase tracking-wider">OVERDUE</div>}
                    </td>
                    <td className={`px-3 py-2 text-right num tabular-nums font-medium ${r.type === "income" ? "text-emerald-300" : "text-red-300"}`}>
                      {r.type === "income" ? "+" : "−"} {fmtMoney(r.amount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => onRunNow(r)}
                        title="Generate next entry now"
                        className="inline-block p-1 text-emerald-300 hover:text-emerald-200"
                        data-testid={`acc-recurring-run-${r.id}`}>
                        <Play className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => onToggle(r)}
                        title={r.is_active ? "Pause" : "Resume"}
                        className="inline-block p-1 text-slate-400 hover:text-slate-100">
                        {r.is_active ? <span className="text-[10px]">⏸</span> : <span className="text-[10px]">▶</span>}
                      </button>
                      <button onClick={() => onEdit(r)}
                        className="inline-block p-1 text-slate-400 hover:text-slate-100">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      {isOwner && (
                        <button onClick={() => onDelete(r)}
                          className="inline-block p-1 text-red-400 hover:text-red-300">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Companies tab
// ============================================================================
function CompaniesTab({ companies, isOwner, onNew, onEdit, onDelete }) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400">
          Your own business entities. Manually added — independent of the Clients / Payouts modules.
        </div>
        <Button onClick={onNew} className="h-9 bg-emerald-600 hover:bg-emerald-500"
          data-testid="acc-new-company-btn">
          <Plus className="w-4 h-4 mr-1" /> New company
        </Button>
      </div>
      {companies.length === 0 ? (
        <div className="rounded-lg border hairline surface-1 text-center py-14 px-6">
          <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-2" />
          <div className="text-slate-300 text-sm font-medium">No companies yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Add Mercy Consultancy, Maro, Mercy Success Sdn Bhd, Nikko, etc.
          </div>
        </div>
      ) : (
        <div className="rounded-lg border hairline surface-1 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Registration</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Address</th>
                <th className="text-right px-3 py-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-t border-slate-800/60 hover:bg-slate-800/20"
                  data-testid={`acc-company-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {c.name}
                    {!c.is_active && <span className="ml-2 text-[10px] text-slate-500">(inactive)</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-400 num hidden md:table-cell">{c.registration_no || "—"}</td>
                  <td className="px-3 py-2 text-slate-400 hidden lg:table-cell truncate">{c.address || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onEdit(c)}
                      className="inline-block p-1 text-slate-400 hover:text-slate-100">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    {isOwner && (
                      <button onClick={() => onDelete(c)}
                        className="inline-block p-1 text-red-400 hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Categories tab
// ============================================================================
function CategoriesTab({ categories, isOwner, onNew, onDelete }) {
  const income = categories.filter((c) => c.kind === "income");
  const expense = categories.filter((c) => c.kind === "expense");
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400">
          Edit the category list. Defaults (Rent, Levy, Salary, Client Payment, etc.) auto-seed on first use.
        </div>
        <Button onClick={onNew} className="h-9 bg-emerald-600 hover:bg-emerald-500"
          data-testid="acc-new-category-btn">
          <Plus className="w-4 h-4 mr-1" /> New category
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CategoryGroup title="Income" list={income} tone="emerald" isOwner={isOwner} onDelete={onDelete} />
        <CategoryGroup title="Expense" list={expense} tone="red" isOwner={isOwner} onDelete={onDelete} />
      </div>
    </>
  );
}

function CategoryGroup({ title, list, tone, isOwner, onDelete }) {
  const dot = tone === "emerald" ? "bg-emerald-400" : "bg-red-400";
  return (
    <div className="rounded-lg border hairline surface-1 p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
        {title} ({list.length})
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-slate-600 italic py-3 text-center">None</div>
      ) : (
        <div className="space-y-1">
          {list.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-800/30 text-sm">
              <span className="text-slate-300">{c.name}</span>
              {isOwner && (
                <button onClick={() => onDelete(c)}
                  className="text-red-400 hover:text-red-300 p-0.5">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Dialogs
// ============================================================================
function EntryDialog({ open, state, companies, categories, onClose, onSaved }) {
  const editing = state?.mode === "edit";
  const [form, setForm] = useState(blankEntry());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const r = state.row;
      setForm({
        date: r.date, type: r.type, amount: String(r.amount ?? ""),
        description: r.description || "", company_id: r.company_id,
        category: r.category || "", payment_method: r.payment_method || "Bank Transfer",
        payment_reference: r.payment_reference || "", vendor_name: r.vendor_name || "",
        tax_amount: String(r.tax_amount ?? "0"), tax_rate: String(r.tax_rate ?? "0"),
      });
    } else {
      setForm(blankEntry());
    }
    setFile(null);
  }, [open, state, editing]);

  function blankEntry() {
    return {
      date: new Date().toISOString().slice(0, 10),
      type: "expense", amount: "", description: "",
      company_id: "", category: "", payment_method: "Bank Transfer",
      payment_reference: "", vendor_name: "", tax_amount: "0", tax_rate: "0",
    };
  }

  const filteredCats = categories.filter((c) => c.kind === form.type);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const b64 = await fileToB64(f);
    setFile({ filename: f.name, content_type: f.type || "application/octet-stream", data_base64: b64 });
  };

  const submit = async () => {
    if (!form.date) { toast.error("Date required"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    if (!form.company_id) { toast.error("Pick a company"); return; }
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: amt,
        tax_amount: parseFloat(form.tax_amount) || 0,
        tax_rate: parseFloat(form.tax_rate) || 0,
      };
      if (file) payload.receipt = file;
      if (editing) {
        await api.put(`/accounting/entries/${state.row.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/accounting/entries`, payload);
        toast.success(`${form.type === "income" ? "Income" : "Expense"} recorded`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg"
        data-testid="acc-entry-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit entry" : "New ledger entry"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setForm({ ...form, type: "income", category: "" })}
              data-testid="entry-type-income"
              className={`p-2 rounded text-sm font-medium border transition flex items-center justify-center gap-1.5
                ${form.type === "income"
                  ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                  : "bg-slate-800/30 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
              <ArrowUpRight className="w-3.5 h-3.5" /> Income
            </button>
            <button onClick={() => setForm({ ...form, type: "expense", category: "" })}
              data-testid="entry-type-expense"
              className={`p-2 rounded text-sm font-medium border transition flex items-center justify-center gap-1.5
                ${form.type === "expense"
                  ? "bg-red-500/20 border-red-500/50 text-red-200"
                  : "bg-slate-800/30 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
              <ArrowDownRight className="w-3.5 h-3.5" /> Expense
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldDate label="Date *" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="entry-date" />
            <FieldNumber label="Amount (RM) *" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="entry-amount" />
          </div>
          <FieldText label="Description *" value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            placeholder="What was this for?" testid="entry-description" />
          <div className="grid grid-cols-2 gap-2">
            <FieldSelect label="Company *" value={form.company_id}
              onChange={(v) => setForm({ ...form, company_id: v })}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
              testid="entry-company" />
            <FieldSelect label="Category" value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={[{ value: "", label: "(none)" }, ...filteredCats.map((c) => ({ value: c.name, label: c.name }))]}
              testid="entry-category" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldSelect label="Method" value={form.payment_method}
              onChange={(v) => setForm({ ...form, payment_method: v })}
              options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
              testid="entry-method" />
            <FieldText label="Reference / txn #" value={form.payment_reference}
              onChange={(v) => setForm({ ...form, payment_reference: v })}
              placeholder="optional" />
          </div>
          <FieldText label={form.type === "income" ? "Payer / Source" : "Vendor"}
            value={form.vendor_name}
            onChange={(v) => setForm({ ...form, vendor_name: v })}
            placeholder="optional" />
          <div className="grid grid-cols-2 gap-2">
            <FieldNumber label="Tax amount (RM)" value={form.tax_amount}
              onChange={(v) => setForm({ ...form, tax_amount: v })} />
            <FieldNumber label="Tax rate (%)" value={form.tax_rate}
              onChange={(v) => setForm({ ...form, tax_rate: v })} />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Receipt / invoice (optional)</Label>
            <input type="file" accept="image/*,application/pdf" onChange={onFile}
              className="block w-full mt-1 text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-slate-700 file:text-slate-100 file:px-2 file:py-1 file:text-xs hover:file:bg-slate-600"
              data-testid="entry-receipt" />
            {file && <div className="text-[11px] text-emerald-300 mt-1">✓ {file.filename}</div>}
            {editing && state?.row?.receipt_filename && !file && (
              <div className="text-[11px] text-slate-500 mt-1">
                Current: {state.row.receipt_filename}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={form.type === "income" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}
            data-testid="entry-save">
            {busy ? "Saving..." : editing ? "Save" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyDialog({ open, state, onClose, onSaved }) {
  const editing = state?.mode === "edit";
  const [form, setForm] = useState({ name: "", registration_no: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { name: state.row.name || "", registration_no: state.row.registration_no || "",
          address: state.row.address || "", notes: state.row.notes || "" }
      : { name: "", registration_no: "", address: "", notes: "" });
  }, [open, state, editing]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/accounting/companies/${state.row.id}`, form);
      else await api.post(`/accounting/companies`, form);
      toast.success(editing ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
        data-testid="acc-company-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit company" : "New business entity"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <FieldText label="Name *" value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            placeholder="e.g. Mercy Consultancy"
            testid="company-name" />
          <FieldText label="Registration / SSM no." value={form.registration_no}
            onChange={(v) => setForm({ ...form, registration_no: v })}
            placeholder="optional" />
          <FieldText label="Address" value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
            placeholder="optional" />
          <FieldText label="Notes" value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="company-save">
            {editing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecurringDialog({ open, state, companies, categories, onClose, onSaved }) {
  const editing = state?.mode === "edit";
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  function blank() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return {
      name: "", type: "expense", amount: "", description: "",
      company_id: "", category: "", payment_method: "Bank Transfer",
      vendor_name: "", frequency: "monthly",
      next_due_date: d.toISOString().slice(0, 10),
      reminder_days_before: "3",
    };
  }

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const r = state.row;
      setForm({
        name: r.name || "", type: r.type, amount: String(r.amount ?? ""),
        description: r.description || "", company_id: r.company_id,
        category: r.category || "", payment_method: r.payment_method || "Bank Transfer",
        vendor_name: r.vendor_name || "", frequency: r.frequency,
        next_due_date: r.next_due_date,
        reminder_days_before: String(r.reminder_days_before ?? "3"),
      });
    } else {
      setForm(blank());
    }
  }, [open, state, editing]);

  const filteredCats = categories.filter((c) => c.kind === form.type);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    if (!form.company_id) { toast.error("Pick a company"); return; }
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: amt,
        reminder_days_before: parseInt(form.reminder_days_before) || 0,
      };
      if (editing) await api.put(`/accounting/recurring/${state.row.id}`, payload);
      else await api.post(`/accounting/recurring`, payload);
      toast.success(editing ? "Updated" : "Rule created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg"
        data-testid="acc-recurring-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit recurring rule" : "New recurring rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <FieldText label="Rule name *" value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            placeholder="e.g. KL Office Rent"
            testid="recur-name" />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setForm({ ...form, type: "income", category: "" })}
              className={`p-2 rounded text-sm font-medium border transition
                ${form.type === "income"
                  ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                  : "bg-slate-800/30 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
              ➕ Income
            </button>
            <button onClick={() => setForm({ ...form, type: "expense", category: "" })}
              className={`p-2 rounded text-sm font-medium border transition
                ${form.type === "expense"
                  ? "bg-red-500/20 border-red-500/50 text-red-200"
                  : "bg-slate-800/30 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
              ➖ Expense
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldNumber label="Amount (RM) *" value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })}
              testid="recur-amount" />
            <FieldSelect label="Frequency *" value={form.frequency}
              onChange={(v) => setForm({ ...form, frequency: v })}
              options={FREQUENCIES.map((f) => ({ value: f, label: f.charAt(0).toUpperCase() + f.slice(1) }))}
              testid="recur-frequency" />
          </div>
          <FieldText label="Description *" value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            placeholder="Appears on each auto-generated entry"
            testid="recur-description" />
          <div className="grid grid-cols-2 gap-2">
            <FieldSelect label="Company *" value={form.company_id}
              onChange={(v) => setForm({ ...form, company_id: v })}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
              testid="recur-company" />
            <FieldSelect label="Category" value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={[{ value: "", label: "(none)" }, ...filteredCats.map((c) => ({ value: c.name, label: c.name }))]} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldDate label="Next due date *" value={form.next_due_date}
              onChange={(v) => setForm({ ...form, next_due_date: v })}
              testid="recur-next-due" />
            <FieldNumber label="WhatsApp reminder (days before)" value={form.reminder_days_before}
              onChange={(v) => setForm({ ...form, reminder_days_before: v })}
              testid="recur-reminder" />
          </div>
          <FieldSelect label="Method" value={form.payment_method}
            onChange={(v) => setForm({ ...form, payment_method: v })}
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))} />
          <FieldText label="Vendor / Payer" value={form.vendor_name}
            onChange={(v) => setForm({ ...form, vendor_name: v })}
            placeholder="optional" />
          <div className="text-[11px] text-slate-500 leading-relaxed">
            <Bell className="w-3 h-3 inline mr-1" />
            A WhatsApp reminder goes out to all eligible recipients
            <strong className="text-slate-300"> {form.reminder_days_before || 0} days</strong> before
            the next due date. Recurring entries auto-materialize at 8 AM Malaysia time.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="recur-save">
            {editing ? "Save" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("expense");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setKind("expense"); }
  }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post(`/accounting/categories`, { name: name.trim(), kind });
      toast.success("Added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-sm"
        data-testid="acc-category-dialog">
        <DialogHeader><DialogTitle>New category</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <FieldText label="Name *" value={name} onChange={setName}
            placeholder="e.g. Office Supplies"
            testid="category-name" />
          <FieldSelect label="Kind" value={kind} onChange={setKind}
            options={[{ value: "income", label: "Income" }, { value: "expense", label: "Expense" }]} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="category-save">
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Mini form fields — keep the dialogs short
// ============================================================================
function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0a0e1a] border-slate-800 mt-1"
        data-testid={testid} />
    </div>
  );
}
function FieldNumber({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#0a0e1a] border-slate-800 mt-1 num"
        data-testid={testid} />
    </div>
  );
}
function FieldDate({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#0a0e1a] border-slate-800 mt-1 num"
        data-testid={testid} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1"
        data-testid={testid}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
